import debounce from 'lodash.debounce';
import { compose, Either, Reader } from 'monet';
import {
  createDetachedSignature,
  createHash,
  getUserCertificates,
  isValidSystemSetup,
} from 'crypto-pro-js';
import { VUE_APP_ENV } from '@/transformEnv';

/**
 * @typedef {import('crypto-pro-js').Certificate} CryptoProCertificate
 */

/**
 * @description Выбрать по необходимости заглушку.
 * @param {function} func
 * @param {function} stub
 * @returns {function}
 */
const orStub = ((needStub) => (func, stub) => (needStub ? stub : func))(
  VUE_APP_ENV === 'local', // for development
);

/**
 * @type {createDetachedSignature}
 */
const pluginCreateDetachedSignature = orStub(
  createDetachedSignature,
  () => Promise.resolve('stubdetachedsignature'),
);

/**
 * @type {createHash}
 */
const pluginCreateHash = orStub(
  createHash,
  () => Promise.resolve('stubhash'.repeat(8)),
);

/**
 * @type {getUserCertificates}
 */
const pluginGetUserCertificates = orStub(
  getUserCertificates,
  () => Promise.resolve([
    { name: 'stubcert1', thumbprint: 'SA1B1' },
    { name: 'stubcert2', thumbprint: 'SA1B2' },
    { name: 'stubcert3', thumbprint: 'SA1B3' },
  ]),
);

/**
 * @type {isValidSystemSetup}
 */
const pluginIsValidSystemSetup = orStub(
  isValidSystemSetup,
  () => Promise.resolve(true),
);

/**
 * @description Проверить готовность ЭЦП.
 * @returns {Promise<Either<boolean,Error>>}
 */
const checkReadiness = compose(Either.fromPromise, pluginIsValidSystemSetup);

/**
 * @description Привести сертификат к доступному для показа.
 * @param {CryptoProCertificate} certificate Сертификат ЭЦП.
 * @returns {object}
 */
const toViewableCertificate = (certificate) => ({
  title: certificate.name,
  value: certificate.thumbprint,
});

/**
 * @description Отобразить показ сертификатов к сертификатам для показа.
 * @param {Either<CryptoProCertificate[]>} result
 * @returns {Either<object[]>}
 */
const mapCertificatesToView = (result) => result.map(
  (list) => list.map(toViewableCertificate),
);

/**
 * @description Перечислить доступные к показу сертификаты.
 * @returns {Promise<Either<object[],Error>>}
 */
const listCertificates = compose(Either.fromPromise, pluginGetUserCertificates);

/**
 * @description Ожидание проверки готовности ЭЦП для загрузки документа.
 * @type {Reader}
 */
const afterCheckToFetchDocument = Reader(({
  fetchUnencryptedDocument,
  showError,
}) => (promise) => {
  promise.then((result) => {
    result.forEach(fetchUnencryptedDocument);
    result.forEachLeft(showError);
  });
  return promise;
});

/**
 * @description Ожидание проверки готовности ЭЦП для установки сертификатов.
 * @type {Reader}
 */
const afterCheckToSetupCerts = Reader(({ replaceCertificates, showError }) => {
  const setupCertificates = compose(
    (promise) => {
      promise
        .then(mapCertificatesToView)
        .then((result) => { result.forEach(replaceCertificates); });
    },
    listCertificates,
  );
  return (promise) => {
    promise.then((result) => {
      result.forEach(setupCertificates);
      result.forEachLeft(showError);
    });
    return promise;
  };
});

/**
 * @description Инициализация подписи.
 * @type {Reader}
 */
const signatureIniting = Reader(checkReadiness)
  .ap(afterCheckToFetchDocument)
  .ap(afterCheckToSetupCerts);

/**
 * @description Выгрузка документа и получение открепленной подписи.
 * @type {Reader}
 */
const signatureUploadAndSignDocument = Reader(
  ({ resolveUnencryptedDocument }) => resolveUnencryptedDocument(),
)
  .map(async (documentFetching) => {
    const result = await documentFetching;
    const { document } = await result.toPromise();
    return pluginCreateHash(document);
  })
  .ap(
    Reader(({ resolveCertificate }) => async (documentHashing) => {
      const certificate = resolveCertificate();
      const hash = await documentHashing;
      return { certificate, hash };
    }),
  )
  .map(async (certificateAndHashResolving) => {
    const { certificate, hash } = await certificateAndHashResolving;
    const signature = await pluginCreateDetachedSignature(certificate, hash);
    return { hash, signature };
  })
  .ap(
    Reader(({ sendHashWithDetachedSignature }) => async (documentSigning) => {
      const { hash, signature } = await documentSigning;
      const result = await sendHashWithDetachedSignature(hash, signature).toPromise();
      const { title } = await result.toPromise();
      return title;
    }),
  )
  .ap(
    Reader(({ showError, showSuccess }) => (process) => {
      process.then(showSuccess, showError);
    }),
  );

/**
 * @description Примесь для создания подписи через провайдер CryptoPro.
 * @type Vue
 */
export default {
  computed: {
    /**
     * @description Требуется выбор сертификата.
     */
    needsCryptoProCertificateSelection() {
      return this.cryptoProSignatureCertificates.length !== 0;
    },
  },
  data() {
    return {
      /**
       * @description Доступные для показа и выбора сертификаты.
       */
      cryptoProSignatureCertificates: [],
      /**
       * @description Получение нешифрованного документа.
       * @type {(function:Promise<Either>|null)}
       */
      cryptoProSignatureFetchUnencryptedDocument: null,
      /**
       * @description Выбранный сертификат ЭЦП.
       * @type {string}
       */
      cryptoProSignatureSelectedCertificate: '',
      /**
       * @description Отправка хэша и открепленной подписи документа.
       * @type {(function:Promise<Either>|null)}
       */
      cryptoProSignatureSendHashWithDetachedSignature: null,
    };
  },
  methods: {
    /**
     * @description Обработать отмену выбора сертификата.
     * @returns {void}
     */
    onCancelCryptoProCertificateSelection() {
      this.cryptoProSignatureCertificates = [];
    },
    /**
     * @description Обработать выгрузку и подпись документа.
     * @returns {void}
     */
    onCryptoProUploadAndSignDocument: debounce(function _() {
      this.onCancelCryptoProCertificateSelection();
      this.runCryptoProSignatureUploadAndSignDocument();
    }, 300),
    /**
     * @description Обработать выбора сертификата для подписи.
     * @param {string} value
     * @returns {void}
     */
    onSelectCryptoProSignatureCertificate(value) {
      this.cryptoProSignatureSelectedCertificate = value;
      this.onCryptoProUploadAndSignDocument();
    },
    /**
     * @description Запустить формирование открепленной подписи.
     * @param {function: Promise<Either>} fetchUnencryptedDocument
     * @param {function: Promise<Either>} sendHashWithDetachedSignature
     * @returns {void}
     */
    runCryptoProSignatureCreating(
      fetchUnencryptedDocument,
      sendHashWithDetachedSignature,
    ) {
      this.cryptoProSignatureSelectedCertificate = '';
      this.cryptoProSignatureFetchUnencryptedDocument = fetchUnencryptedDocument;
      this.cryptoProSignatureSendHashWithDetachedSignature = sendHashWithDetachedSignature;
      signatureIniting.run({
        fetchUnencryptedDocument: this.cryptoProSignatureFetchUnencryptedDocument,
        replaceCertificates: (items) => {
          if (items.length === 0) {
            this.notifyFail('Список сертификатов, доступных пользователю в системе, пуст');
          }
          this.cryptoProSignatureCertificates = items;
        },
        showError: this.notifyFail,
      });
    },
    /**
     * @description Запустить выгрузку и подпись документа.
     * @returns {void}
     */
    runCryptoProSignatureUploadAndSignDocument() {
      signatureUploadAndSignDocument.run({
        resolveUnencryptedDocument: this.cryptoProSignatureFetchUnencryptedDocument,
        resolveCertificate: () => this.cryptoProSignatureSelectedCertificate,
        sendHashWithDetachedSignature: this.cryptoProSignatureSendHashWithDetachedSignature,
        showError: this.notifyFail,
        showSuccess: this.notifySuccess,
      });
    },
  },
};
